"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { ChatHeader } from "./ChatHeader"
import { ChatInput } from "./ChatInput"
import { ChatMessages } from "./ChatMessages"
import { ArtifactEvent, Message, MessageSegment, ToolCall } from "./types"

import { useGlobal } from "@/app/context/GlobalContext"
import { AgentCoreClient, imageFormatFor } from "@/lib/agentcore-client"
import type { AgentPattern, UploadReference } from "@/lib/agentcore-client"
import { LOCAL_AGENT, LOCAL_AGENT_PATTERN } from "@/lib/localDev"
import { submitFeedback } from "@/services/feedbackService"
import { reportClientError } from "@/services/clientErrorBeacon"
import type { ClientErrorPhase } from "@/services/clientErrorBeacon"
import { fetchArtifactDownloadUrl } from "@/services/artifactService"
import { uploadFile } from "@/services/uploadService"
import { useAuth } from "react-oidc-context"
import { useDefaultTool, useToolRenderer } from "@/hooks/useToolRenderer"
import { ToolCallDisplay } from "./ToolCallDisplay"
import { LoadModuleTool, RunScriptTool, RunCodeTool } from "./AgentTools"
import { AppSidebar } from "./AppSidebar"
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion"
import { copy } from "@/lib/copy"
import logoAskme from "@/assets/brand/ASKme-LOGO2x.png"
import logoAskmeWhite from "@/assets/brand/ASKme-LOGO2x-white.png"
import { useIsDark } from "@/components/theme/theme"
import suggestionsIcon from "@/assets/brand/suggestions-icon.gif"
import suggestionsIconWhite from "@/assets/brand/suggestions-white.gif"
import { initialStatusLabel, processingStatusLabel } from "@/lib/attachmentStatus"
import { bodyBudget, maxBytesFor, oversizedFiles, promptOverflowBytes } from "@/lib/attachmentBudget"
import { DEFAULT_EFFORT } from "@/lib/models"
import { useSessions } from "@/hooks/useSessions"
import { createSessionStore } from "@/services/createSessionStore"

/**
 * Return up to `count` random, non-repeating items from `items`, preserving
 * none of the original order (Fisher–Yates over a copy). Used to rotate the
 * empty-state suggestion pills so the same four prompts don't show on every
 * visit — the full bank still only contains vetted, domain-vocabulary
 * questions (see copy.ts), this just varies which subset is shown.
 *
 * `avoid` lets the caller exclude the previously-shown set so a re-roll is
 * guaranteed to differ from the last four (variety on every new chat). If
 * excluding `avoid` would leave fewer than `count` candidates the full bank
 * is used instead, so the caller always gets `count` items.
 */
function pickRandom<T>(items: readonly T[], count: number, avoid: readonly T[] = []): T[] {
  const avoidSet = new Set(avoid)
  let pool = items.filter(x => !avoidSet.has(x))
  if (pool.length < count) pool = [...items]
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pool[i], pool[j]] = [pool[j], pool[i]]
  }
  return pool.slice(0, count)
}

export default function ChatInterface() {
  const isDark = useIsDark()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState("")
  // In-session image thumbnails, keyed by message id. Object URLs live only for
  // the current session (never persisted to the transcript, which stays
  // metadata-only) and are revoked on unmount to avoid blob leaks.
  const [messagePreviews, setMessagePreviews] = useState<Record<string, string[]>>({})
  const messagePreviewsRef = useRef(messagePreviews)
  messagePreviewsRef.current = messagePreviews
  useEffect(() => {
    return () => {
      Object.values(messagePreviewsRef.current).forEach(urls =>
        urls.forEach(u => URL.revokeObjectURL(u)),
      )
    }
  }, [])
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<AgentCoreClient | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const { isLoading, setIsLoading, selectedModel, selectedEffort } = useGlobal()
  const auth = useAuth()

  // Per-user key for scoping saved history. Cognito `sub` is the durable
  // identifier; email is a readable fallback; "local" covers local-agent mode
  // where there is no signed-in user.
  const profile = (auth.user?.profile ?? null) as { sub?: string; email?: string; name?: string } | null
  const ownerKey = profile?.sub ?? profile?.email ?? "local"
  const userName = profile?.name ?? profile?.email ?? ""

  // Live ref to auth so the memoized store's token getter always reads the
  // current ID token, even after a token refresh.
  const authRef = useRef(auth)
  authRef.current = auth

  // Conversation history behind the SessionStore seam. The factory picks the
  // backend for this environment (local browser vs the server-backed sessions
  // API); rebuilt only if the signed-in user changes.
  // Per-feature availability of the deployed REST routes (each off until it
  // ships), read from aws-exports.json. Until enabled: history uses the
  // browser-local store, the preferences menu is hidden, and the error beacon
  // is a local console warning.
  const [sessionsApi, setSessionsApi] = useState(false)
  const [preferencesApi, setPreferencesApi] = useState(false)
  const [clientErrorApi, setClientErrorApi] = useState(false)

  const sessionStore = useMemo(
    () =>
      createSessionStore({
        ownerKey,
        getIdToken: () => authRef.current.user?.id_token,
        remoteEnabled: sessionsApi,
      }),
    [ownerKey, sessionsApi]
  )
  const {
    summaries,
    activeId,
    startNew,
    open,
    persist,
    remove,
    rename,
    touch,
    addOptimistic,
    reconcileOptimistic,
  } = useSessions(sessionStore)
  // The active conversation id doubles as the agent's runtime session id.
  const sessionId = activeId

  // Live mirror of the active id so async stream callbacks (which captured a
  // fixed send-time id) can compare against whatever conversation is on screen
  // *now* — used to gate the error banner and the persist to the foreground.
  const activeIdRef = useRef(activeId)
  activeIdRef.current = activeId

  // A random subset of the starter-prompt bank, re-rolled per conversation
  // (keyed on activeId) so every new chat shows a fresh four — and guaranteed
  // to differ from the previous set (prevSuggestionsRef fed as `avoid`), so a
  // re-roll never repeats the same pills. Every question still comes from the
  // vetted domain-vocabulary bank in copy.ts (never freeform/generated).
  const prevSuggestionsRef = useRef<string[]>([])
  const suggestionPills = useMemo(() => {
    const next = pickRandom(copy.suggestions, 4, prevSuggestionsRef.current)
    prevSuggestionsRef.current = next
    return next
    // activeId in deps: a new/switched conversation re-rolls the pills.
  }, [activeId])

  // Holds the in-flight request's AbortController so the user can stop it.
  const abortRef = useRef<AbortController | null>(null)

  // Latest transcript, mirrored into a ref so the turn-completion effect can
  // read it without itself depending on `messages` (which would fire it on
  // every streamed token).
  const messagesRef = useRef<Message[]>(messages)
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // The single in-flight turn (global `isLoading` allows only one at a time in
  // this stage): its conversation id plus a live snapshot of its assistant
  // message. `sessionId` lets the persist effect tell whether the finished
  // turn's conversation is still on screen; `assistant` lets a switch-back into
  // a still-streaming conversation re-append the live bubble (Memory has the
  // committed messages but not the one still streaming), so the loader shows and
  // — because writers target by message id — subsequent tokens repaint it live.
  // Null when no turn is in flight.
  const inFlightTurnRef = useRef<{
    sessionId: string
    user: Message
    assistant: Message
  } | null>(null)

  // Session id of the in-flight turn, mirrored into state (not just the ref
  // above) so the sidebar can re-render a per-row streaming indicator. Null when
  // no turn is streaming.
  const [streamingId, setStreamingId] = useState<string | null>(null)

  // Persist the active conversation only when a turn finishes (loading goes
  // true → false), and only when the just-finished turn's conversation is still
  // the one on screen. Keying off the loading edge — not off `messages` — means
  // merely opening a saved conversation does not re-save and bump its recency.
  // Persist is the only durable write in the LOCAL store (in the cloud `save`
  // is a no-op). Guarding on the send-time id means a turn that completed in the
  // background (the user switched away) does NOT re-save/recency-bump whatever
  // conversation is now foreground; in the cloud that turn is already in Memory,
  // and its recency was bumped at send.
  const prevLoadingRef = useRef(isLoading)
  useEffect(() => {
    const turnFinished = prevLoadingRef.current && !isLoading
    prevLoadingRef.current = isLoading
    if (!turnFinished) return
    const finishedSession = inFlightTurnRef.current?.sessionId ?? null
    inFlightTurnRef.current = null
    setStreamingId(null)
    if (!finishedSession) return
    // Always reconcile the optimistic row for the finished turn (re-lists so a
    // committed new conversation shows its real summary, or a never-persisted
    // one drops out). Then persist only when this turn's conversation is still
    // on screen — a background completion (user switched away) must not re-save
    // or recency-bump the foreground conversation.
    void reconcileOptimistic(finishedSession)
    if (messagesRef.current.length > 0 && finishedSession === activeIdRef.current) {
      void persist(messagesRef.current)
    }
  }, [isLoading, persist, reconcileOptimistic])

  // Register the default tool renderer (wildcard "*") and example tailored
  // renderers. Adapt these to the tool names your own agent emits.
  useDefaultTool(({ name, args, status, result }) => (
    <ToolCallDisplay name={name} args={args} status={status} result={result} />
  ))
  useToolRenderer("read_skill", props => <LoadModuleTool {...props} />)
  useToolRenderer("run_script", props => <RunScriptTool {...props} />)
  useToolRenderer("run_code", props => <RunCodeTool {...props} />)

  // Load agent configuration and create client on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        // Local mode: skip aws-exports.json entirely. There is no deployed
        // runtime ARN — the client posts to the Vite-proxied /invocations.
        if (LOCAL_AGENT) {
          setClient(
            new AgentCoreClient({
              pattern: LOCAL_AGENT_PATTERN as AgentPattern,
            })
          )
          return
        }

        const response = await fetch("/aws-exports.json")
        if (!response.ok) {
          throw new Error("Failed to load configuration")
        }
        const config = await response.json()

        // Enable each deployed REST route independently, only when the deployed
        // API actually has it (they ship as separate features).
        setSessionsApi(config.sessionsApiEnabled === true)
        setPreferencesApi(config.preferencesApiEnabled === true)
        setClientErrorApi(config.clientErrorApiEnabled === true)

        // El guard es sobre lo que de verdad hace falta. Antes exigía
        // `config.agentRuntimeArn` y tiraba antes de construir el cliente — en el
        // camino ACTIVO, no en un fallback—, así que el ARN era obligatorio para que
        // el chat arrancara aunque nunca se usara para llamar a nada. Eso forzaba a
        // publicar el ARN (y el account id) en un `aws-exports.json` de lectura
        // pública. Lo que el turno necesita es la ruta REST.
        if (!config.feedbackApiUrl) {
          throw new Error("API endpoint not found in configuration")
        }

        const agentClient = new AgentCoreClient({
          pattern: (config.agentPattern || "strands-single-agent") as AgentPattern,
          // Los turnos van por la ruta /chat de nuestro API Gateway (SSE). El
          // navegador no puede invocar el runtime directamente: no tiene authorizer
          // JWT. Lo invoca el Lambda de /chat con credenciales IAM.
          agentApiUrl: `${config.feedbackApiUrl}chat`,
        })

        setClient(agentClient)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(`${copy.errors.configPrefix}: ${errorMessage}`)
        console.error("Failed to load agent configuration:", err)
      }
    }

    loadConfig()
  }, [])

  const sendMessage = async (userMessage: string, files: File[] = []) => {
    if (!userMessage.trim() || !client) return

    // Dos comprobaciones ANTES de tocar el estado: si el turno no puede salir, no
    // debe quedar una burbuja huérfana ni un "Analizando…" que nunca termina. Y
    // antes de subir a S3, para no hacer esperar una subida que va a ser rechazada.
    //
    // 1. Cada archivo contra el techo de su tipo (3.75 MB imágenes / 4.5 MB
    //    documentos), que es el que aplica el runtime. Sin esto, el rechazo llega
    //    después de la subida y de que el turno arrancó.
    const oversized = oversizedFiles(files)
    if (oversized.length) {
      const maxMB = Math.min(...oversized.map(f => maxBytesFor(f))) / 1_000_000
      setError(
        copy.input.attachFileTooLarge(
          oversized.map(f => f.name),
          maxMB
        )
      )
      return
    }

    // 2. El texto contra los 8 KB de `SizeRestrictions_BODY` en el WebACL de
    //    CloudFront. Los adjuntos ya no cuentan —van por S3— así que lo único que
    //    puede pasarse es un prompt muy largo (un brief pegado, típicamente). Sin
    //    este aviso, CloudFront responde un 403 de HTML que no se parece en nada a
    //    un problema de longitud.
    const body = bodyBudget(userMessage)
    if (body.exceeds) {
      setError(copy.input.promptTooLong(promptOverflowBytes(userMessage)))
      return
    }

    // Capture the conversation id at send time. The sidebar should reflect the
    // conversation the user just acted on, so we bump recency now (not at turn
    // completion) and key it to this id — if the user switches conversations
    // while the turn streams, the bump still lands on the right one.
    const turnSessionId = activeId

    // Clear any previous errors
    setError(null)

    // Add user message to chat.
    // `attachments` guarda solo METADATO (nombre/tipo/tamaño), nunca los bytes:
    // sin este campo el adjunto desaparecía del transcript al enviar y, al reabrir
    // la conversación, no quedaba rastro de que la respuesta se calculó con un
    // documento (§6 estado 4).
    const newUserMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString(),
      ...(files.length
        ? {
            attachments: files.map(f => ({
              filename: f.name,
              contentType: f.type || "application/octet-stream",
              bytes: f.size,
              lane: imageFormatFor(f) !== undefined ? ("image" as const) : ("document" as const),
            })),
          }
        : {}),
    }

    setMessages(prev => [...prev, newUserMessage])
    setInput("")
    setIsLoading(true)

    // Stage in-session thumbnails for image attachments (live preview only, not
    // persisted). Keyed to this message's id so ChatMessage can render them.
    const imageFiles = files.filter(f => imageFormatFor(f) !== undefined)
    if (imageFiles.length) {
      const urls = imageFiles.map(f => URL.createObjectURL(f))
      setMessagePreviews(prev => ({ ...prev, [newUserMessage.id]: urls }))
    }

    // Show a brand-new conversation in the sidebar immediately. It isn't in the
    // store's list yet (its first message hasn't been committed), so without an
    // optimistic row it would stay invisible until this turn finishes. Only for
    // a not-yet-listed id; an existing conversation is already shown and just
    // needs the recency bump below. Reconciled by id on the next refresh.
    if (!summaries.some(s => s.id === turnSessionId)) {
      const now = new Date().toISOString()
      const title =
        userMessage.trim().length > 48 ? `${userMessage.trim().slice(0, 48)}…` : userMessage.trim()
      addOptimistic({ id: turnSessionId, title, createdAt: now, updatedAt: now })
    }

    // Raise this conversation to the top of the sidebar right away. Best-effort
    // and keyed to the send-time id (see above).
    void touch(turnSessionId)

    // Create placeholder for assistant response. Its id is the sole target for
    // every writer this turn — so once the user switches away and this array is
    // replaced by another conversation's transcript, every write below finds no
    // matching id and no-ops instead of corrupting the foreground bubble.
    const assistantId = crypto.randomUUID()
    const assistantResponse: Message = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      streaming: true,
      // Etiqueta de fase según el carril REAL del archivo (ADR-DOC-06, gap A).
      // Solo los documentos/hojas se suben a S3; una imagen viaja en el cuerpo del
      // turno, así que anunciarle "Subiendo…" sería describir trabajo inexistente
      // —y llamarla "documento" es simplemente incorrecto.
      ...(files.length ? { statusLabel: initialStatusLabel(files) } : {}),
    }

    setMessages(prev => [...prev, assistantResponse])

    // Register this turn as the in-flight one. `assistant` is a live snapshot
    // (updated by updateMessage) so a switch-back into this still-streaming
    // conversation can re-append the current bubble; `user` lets us restore the
    // prompt too when the local store hasn't persisted the turn yet.
    inFlightTurnRef.current = {
      sessionId: turnSessionId,
      user: newUserMessage,
      assistant: assistantResponse,
    }
    // Mirror to state so the sidebar shows a streaming indicator on this row.
    setStreamingId(turnSessionId)

    // New abort controller per turn; `stopGeneration` triggers it. Declared
    // outside the try so the finally can compare against it before clearing.
    const controller = new AbortController()
    abortRef.current = controller

    // Cheap per-turn transport telemetry for the client-error beacon. Counting
    // the SSE events we already parse below is a proxy for "did any byte reach
    // us this turn": 0 ⇒ the runtime never flushed (a pre-dispatch failure or a
    // delivered pre-stream status). These are plain reads in the callback we
    // already run — no new stream plumbing on the turn path. Declared before the
    // try so both the streamError branch and the catch can read them at failure.
    const turnStartMs = Date.now()
    let framesReceived = 0
    let firstByteMs: number | undefined
    let lastEventMs = turnStartMs
    // A per-turn trace id for the beacon record. The wire X-Amzn-Trace-Id is
    // minted inside the client and not surfaced here, so this identifies the
    // beacon record itself rather than joining to it.
    const beaconTraceId = `1-${Math.floor(turnStartMs / 1000).toString(16)}-${crypto.randomUUID()}`

    // Parse the leading "HTTP <n>:" that the client mints for a delivered
    // non-2xx (a 429/5xx/401 with a body); its absence means a true transport
    // throw that never got a status. Returns undefined when there is no prefix.
    const parseHttpStatus = (message: string): number | undefined => {
      const match = /^HTTP (\d{3})\b/.exec(message)
      return match ? Number(match[1]) : undefined
    }

    // Capture one failed turn: emit a stable-prefix breadcrumb and fire the
    // beacon, both with the same fields. Fire-and-forget — never awaited. The
    // whole body is wrapped so NOTHING here (a synchronous throw from a future
    // refactor, a bad navigator read) can escape into the turn path and change
    // the banner/bubble behavior that follows this call.
    const captureClientError = (errName: string, errMessage: string): void => {
      try {
        const failurePhase: ClientErrorPhase = framesReceived > 0 ? "mid-stream" : "pre-first-byte"
        const httpStatus = parseHttpStatus(errMessage)
        const record = {
          runtimeSessionId: turnSessionId,
          traceId: beaconTraceId,
          failurePhase,
          ...(httpStatus !== undefined ? { httpStatus } : {}),
          errName,
          errMessage,
          framesReceived,
          ...(firstByteMs !== undefined ? { firstByteMs } : {}),
          gapMs: Date.now() - lastEventMs,
          online: navigator.onLine,
          userAgent: navigator.userAgent,
        }
        console.warn("[client-error]", record)
        // Only POST the beacon when the /client-error route is available;
        // otherwise it's a local console warning.
        if (clientErrorApi) void reportClientError(record, auth.user?.id_token)
      } catch {
        // The beacon must never make a failed turn worse.
      }
    }

    try {
      // Get auth token from react-oidc-context. In local mode there is no
      // login — the client substitutes a throwaway token the local shim
      // accepts, so an empty token here is fine.
      // Use the ID token: our API Gateway /chat route uses a Cognito User Pools
      // authorizer that validates the ID token (aud = app client id), same as
      // the other REST services. The access token (token_use=access) is rejected
      // with 401. Local-agent mode has no user; the client substitutes a
      // throwaway token the shim accepts.
      const accessToken = auth.user?.id_token ?? ""

      if (!accessToken && !LOCAL_AGENT) {
        throw new Error(copy.errors.authRequired)
      }

      const segments: MessageSegment[] = []
      const toolCallMap = new Map<string, ToolCall>()
      // Set by the "error" stream event (see the switch below); checked once
      // the stream ends so a backend failure always surfaces, even if some
      // text/tool content already streamed before the error arrived.
      let streamError: string | null = null

      const updateMessage = () => {
        // Build content from text segments for backward compat
        const content = segments
          .filter((s): s is Extract<MessageSegment, { type: "text" }> => s.type === "text")
          .map(s => s.content)
          .join("")

        const nextAssistant: Message = {
          id: assistantId,
          role: "assistant",
          content,
          timestamp: assistantResponse.timestamp,
          segments: [...segments],
          // updateMessage only runs mid-stream, so this bubble is still live.
          // The loader stays until sendMessage's finally clears the flag.
          streaming: true,
        }
        // Keep the in-flight snapshot current so a switch-back re-appends the
        // latest bubble (only while this turn is still the registered one).
        if (inFlightTurnRef.current?.assistant.id === assistantId) {
          inFlightTurnRef.current = {
            ...inFlightTurnRef.current,
            assistant: nextAssistant,
          }
        }

        setMessages(prev => {
          // Target this turn's own assistant bubble by id. If it isn't in the
          // current array (the user switched to another conversation), do
          // nothing — never write onto whatever now sits last.
          const i = prev.findIndex(m => m.id === assistantId)
          if (i === -1) return prev
          const updated = [...prev]
          updated[i] = nextAssistant
          return updated
        })
      }

      // Cambia la etiqueta de fase del loader de ESTE turno. Igual que los demás
      // escritores, apunta por id y no por posición: si el usuario se cambió de
      // conversación, no encuentra el id y no hace nada, en vez de escribir sobre la
      // burbuja equivocada.
      const setStatusLabel = (targetId: string, label: string | undefined) => {
        setMessages(prev => {
          const i = prev.findIndex(m => m.id === targetId)
          if (i === -1) return prev
          const updated = [...prev]
          updated[i] = { ...updated[i], statusLabel: label }
          return updated
        })
      }

      // User identity is extracted server-side from the validated JWT token,
      // not passed as a parameter — prevents impersonation via prompt injection.
      // selectedModel/selectedEffort are read at send time so each turn uses
      // whatever is selected in the dropdowns at that moment (per-turn control).
      // effort is passed as undefined at the default so the client omits it and
      // the turn stays a pure cache hit regardless of the backend config default.
      //
      // TODOS los adjuntos —imágenes incluidas— van por el carril de S3: se suben
      // ahora y en el turno viaja sólo la referencia por key. El runtime las baja y
      // las materializa (`_process_uploads`: bloque `document`, bloque `image`, o
      // archivo en el sandbox según el tipo).
      //
      // POR QUÉ las imágenes ya no viajan en base64 dentro del cuerpo
      // -------------------------------------------------------------
      // El WebACL de CloudFront tiene `AWSManagedRulesCommonRuleSet`, que incluye
      // `SizeRestrictions_BODY`: **bloquea cualquier cuerpo de más de 8 KB**. Una
      // imagen en base64 infla 4/3, así que cualquier imagen de más de ~5.8 KB
      // producía un cuerpo por encima del límite y CloudFront devolvía un 403 de
      // HTML —no un error de la app— antes de que el turno llegara al backend.
      // Medido el 2026-08-18 contra el ambiente desplegado: un JPEG de 36 KB generó
      // un cuerpo de ~48 KB, 6 veces el límite. En la práctica ninguna imagen real
      // funcionaba. Los logs del WAF tienen los BLOCK con
      // `terminatingRuleId=AWS-AWSManagedRulesCommonRuleSet`,
      // `ruleId=SizeRestrictions_BODY`.
      //
      // Mandarlas por S3 deja el cuerpo del turno en unos pocos KB, así que la regla
      // no se activa y no hay que debilitar el WAF para que los adjuntos funcionen.
      // El carril inline (`body.images`) sigue existiendo en el runtime para quien
      // llame la API directo; esta UI ya no lo usa.
      const uploadable = files
      let uploads: UploadReference[] = []
      if (uploadable.length) {
        const idToken = auth.user?.id_token
        if (!idToken) {
          throw new Error(copy.errors.authRequired)
        }
        uploads = await Promise.all(uploadable.map(f => uploadFile(f, sessionId, idToken)))
      }

      // Subida hecha: el tiempo que sigue es del runtime leyendo el archivo.
      // Cambiar la etiqueta aquí distingue "sigue subiendo" de "ya está leyendo", que
      // en un turno de varios minutos es la diferencia entre parecer colgado y
      // parecer trabajando.
      //
      // Aplica a cualquier adjunto: ahora todos tienen las dos fases reales (S3 y
      // después lectura por el runtime), también las imágenes. Antes las imágenes
      // viajaban en el cuerpo y no había segundo hito que anunciar, así que este
      // bloque miraba sólo los documentos.
      if (files.length) {
        // Plural por total de archivos, igual que la etiqueta inicial: se observó
        // "Procesando documento…" en singular con un PDF y un JPG adjuntos.
        setStatusLabel(assistantId, processingStatusLabel(files))
      }

      await client.invoke(
        userMessage,
        sessionId,
        accessToken,
        event => {
          // Cheap transport telemetry for the client-error beacon: every parsed
          // event means at least one byte reached us. firstByteMs is stamped on
          // the first one; lastEventMs tracks the most recent so a failure can
          // report the silent gap that preceded it.
          const nowMs = Date.now()
          if (framesReceived === 0) {
            firstByteMs = nowMs - turnStartMs
            // Llegó el primer byte: la fase de documento terminó y lo que sigue es
            // razonamiento normal. Volver al "Pensando…" genérico evita dejar
            // "Procesando documento…" colgado durante todo el turno.
            if (files.length) setStatusLabel(assistantId, undefined)
          }
          framesReceived += 1
          lastEventMs = nowMs
          switch (event.type) {
            case "reasoning": {
              // Thinking / tool-progress from the OCESA agent. Coalesce ONLY with
              // the immediately-previous segment, which is rule 1 del §3 del
              // protocolo y la misma regla que aplica `turno.js` en el Lambda
              // (`test_las_dos_puntas_coalescan_igual` la fija en las dos puntas).
              // Un `reasoning` que salta sobre un `text` intermedio para pegarse a
              // un panel anterior colapsa TODO el texto del turno en un segmento y
              // vuelve indistinguible el progreso de la respuesta; agrupar los
              // paneles es cosa del render (`groupReasoningForDisplay`), no de la
              // estructura que se guarda.
              const last = segments[segments.length - 1]
              if (last && last.type === "reasoning") {
                last.content += event.content
              } else {
                segments.push({ type: "reasoning", content: event.content })
              }
              updateMessage()
              break
            }
            case "text": {
              // If text arrives after a tool segment, mark all pending tools as complete
              const prev = segments[segments.length - 1]
              if (prev && prev.type === "tool") {
                for (const tc of toolCallMap.values()) {
                  if (tc.status === "streaming" || tc.status === "executing") {
                    tc.status = "complete"
                  }
                }
              }
              // Append to last text segment, or create new one
              const last = segments[segments.length - 1]
              if (last && last.type === "text") {
                last.content += event.content
              } else {
                segments.push({ type: "text", content: event.content })
              }
              updateMessage()
              break
            }
            case "tool_use_start": {
              const tc: ToolCall = {
                toolUseId: event.toolUseId,
                name: event.name,
                input: "",
                status: "streaming",
              }
              toolCallMap.set(event.toolUseId, tc)
              segments.push({ type: "tool", toolCall: tc })
              updateMessage()
              break
            }
            case "tool_use_delta": {
              const tc = toolCallMap.get(event.toolUseId)
              if (tc) {
                tc.input += event.input
              }
              updateMessage()
              break
            }
            case "tool_result": {
              const tc = toolCallMap.get(event.toolUseId)
              if (tc) {
                tc.result = event.result
                tc.status = "complete"
              }
              updateMessage()
              break
            }
            case "message": {
              if (event.role === "assistant") {
                for (const tc of toolCallMap.values()) {
                  if (tc.status === "streaming") tc.status = "executing"
                }
                updateMessage()
              }
              break
            }
            case "artifact": {
              // A deliverable (figure PNG, report, etc.) surfaced after a
              // tool-result. Append as its own segment so it renders inline.
              segments.push({
                type: "artifact",
                artifact: {
                  kind: event.kind,
                  fmt: event.fmt,
                  url: event.url,
                  // Live-renderable payload. This mapping copies fields one by one,
                  // so a new field on the stream event is invisible to the UI until
                  // it is added HERE — `data` reached the browser and was silently
                  // dropped at this line until it was threaded through.
                  data: event.data as ArtifactEvent["data"],
                  name: event.name,
                  bytes: event.bytes,
                  runId: event.runId,
                  backend: event.backend,
                  intendedBackend: event.intendedBackend,
                },
              })
              updateMessage()
              break
            }
            case "error": {
              // A failure reported by the backend mid-stream (e.g. the agent
              // raised, or a prior turn was still processing on this session —
              // strands.types.exceptions.ConcurrencyException). Record it;
              // handled once the stream ends below so it takes precedence
              // over any partial content already rendered.
              streamError = event.message
              break
            }
            case "attachment_error": {
              // El runtime no pudo usar un adjunto. NO es `streamError`: la
              // respuesta sigue llegando y es útil. Se anota en el mensaje del
              // USUARIO (junto a su adjunto, donde el aviso tiene sentido) y se
              // persiste, para que al reabrir siga claro que la respuesta guardada
              // ignora ese archivo.
              setMessages(prev => {
                const i = prev.findIndex(m => m.id === newUserMessage.id)
                if (i === -1) return prev
                const updated = [...prev]
                updated[i] = { ...updated[i], rejectedAttachments: event.rejected }
                return updated
              })
              break
            }
          }
        },
        controller.signal,
        selectedModel,
        selectedEffort === DEFAULT_EFFORT ? undefined : selectedEffort,
        // Sin `images`: los adjuntos van todos por `uploads` (ver el comentario del
        // carril arriba). El parámetro sigue en la firma del cliente porque el
        // runtime todavía acepta el carril inline.
        undefined,
        uploads.length ? uploads : undefined
      )

      // Stream ended (normal completion or abort): finalize any tool left
      // mid-flight so it doesn't freeze on "recibiendo parámetros".
      let toolsFinalized = false
      for (const tc of toolCallMap.values()) {
        if (tc.status !== "complete") {
          tc.status = "complete"
          toolsFinalized = true
        }
      }
      if (toolsFinalized) updateMessage()

      // A backend failure reported mid-stream (see the "error" case above):
      // surface it the same way a thrown/caught error is surfaced below —
      // error banner + a resolved (non-empty) assistant bubble — so the turn
      // never ends stuck on the empty "Pensando…" placeholder with no
      // indication anything went wrong.
      // `streamError` se asigna DENTRO del callback del stream, y el análisis de flujo de
      // TypeScript no rastrea esas asignaciones (microsoft/TypeScript#9998): acá sólo ve el
      // inicializador `null`, así que dentro del `if` la variable se estrecha a `never` y
      // cualquier uso de `string` —`.includes(...)`— no compila. La aserción devuelve el tipo
      // declarado; anotar la constante NO alcanza, porque un `const` toma el tipo estrechado
      // del inicializador y volvería a ser `null`.
      const failure = streamError as string | null
      if (failure) {
        // Capture the failure durably BEFORE surfacing anything. This is a
        // backend-reported error that reached the runtime (it rode the stream),
        // so frames were received; the beacon records it for classification.
        captureClientError("StreamError", failure)
        // Surface the banner only if this turn's conversation is on screen, so
        // a background failure never paints over the foreground conversation.
        if (turnSessionId === activeIdRef.current) {
          // Known issue: toolResult/toolUse mismatch corrupts the session history.
          // Show a user-friendly message instead of the raw ValidationException.
          // https://github.com/strands-agents/harness-sdk/issues/1610
          const isToolResultMismatch =
            failure.includes("toolResult blocks") &&
            failure.includes("exceeds the number of toolUse blocks")
          setError(
            isToolResultMismatch
              ? copy.errors.conversationCorrupted
              : `${copy.errors.responsePrefix}: ${failure}`
          )
        }
        setMessages(prev => {
          const i = prev.findIndex(m => m.id === assistantId)
          if (i === -1) return prev
          const target = prev[i]
          if (target.role === "assistant" && !target.content && !target.segments?.length) {
            const updated = [...prev]
            updated[i] = { ...target, content: copy.errors.generic }
            return updated
          }
          return prev
        })
      } else if (controller.signal.aborted) {
        // If the user stopped before any text/tool/artifact arrived, replace
        // the empty placeholder so it doesn't show the "Pensando…" loader
        // forever.
        setMessages(prev => {
          const i = prev.findIndex(m => m.id === assistantId)
          if (i === -1) return prev
          const target = prev[i]
          if (target.role === "assistant" && !target.content && !target.segments?.length) {
            const updated = [...prev]
            updated[i] = { ...target, content: copy.status.stopped }
            return updated
          }
          return prev
        })
      } else {
        // Normal completion: stamp the assistant bubble with the moment the
        // stream finished (not the placeholder-creation time) and the wall-clock
        // turn duration. Targeted by assistantId so a resolved-late turn never
        // rewrites another conversation's message.
        const completedAt = new Date().toISOString()
        const elapsedMs = Date.now() - turnStartMs
        setMessages(prev => {
          const i = prev.findIndex(m => m.id === assistantId)
          if (i === -1) return prev
          const updated = [...prev]
          updated[i] = { ...updated[i], timestamp: completedAt, elapsedMs }
          return updated
        })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      const errName = err instanceof Error ? err.name : "Unknown"
      console.error("Error invoking AgentCore:", err)

      // Capture the failure durably BEFORE surfacing anything. The banner shows
      // this same string; the beacon persists it (plus the phase/status we can
      // read) so a red-banner turn is classifiable server-side. Fire-and-forget.
      captureClientError(errName, errorMessage)

      // Surface the banner only if this turn's conversation is on screen (a
      // background failure must not paint over the foreground conversation).
      if (turnSessionId === activeIdRef.current) {
        setError(`${copy.errors.responsePrefix}: ${errorMessage}`)
      }

      // Update this turn's own assistant bubble with the error, targeting by
      // id so a resolved-late failure never corrupts another conversation.
      setMessages(prev => {
        const i = prev.findIndex(m => m.id === assistantId)
        if (i === -1) return prev
        const updated = [...prev]
        updated[i] = { ...updated[i], content: copy.errors.generic }
        return updated
      })
    } finally {
      setIsLoading(false)
      // Only clear the shared abort ref if it still points at THIS turn's
      // controller — a newer turn may have replaced it.
      if (abortRef.current === controller) abortRef.current = null
      // The stream is over on every path (completion, backend error, abort,
      // throw): clear the streaming flag so the "Pensando…" loader stops. This
      // is the one place guaranteed to run regardless of outcome, including the
      // branches above that keep already-streamed content (they leave the flag
      // set). Target by id so a resolved-late turn never touches another
      // conversation's bubble.
      setMessages(prev => {
        const i = prev.findIndex(m => m.id === assistantId)
        if (i === -1 || !prev[i].streaming) return prev
        const updated = [...prev]
        // El turno terminó y los segmentos se quedan TAL CUAL llegaron: son el
        // registro de lo que se transmitió, y es la misma forma que `turno.js`
        // guarda en DynamoDB. Unir los paneles de razonamiento es una decisión de
        // presentación y vive en el render (`groupReasoningForDisplay`), aplicada
        // igual a un turno vivo, a uno recién terminado y a uno reabierto — así
        // los tres se ven idénticos sin tocar el dato. `content` ya lo armó
        // `updateMessage` concatenando los segmentos de texto, con el mismo
        // criterio que `textoDeSegmentos` del Lambda.
        updated[i] = { ...updated[i], streaming: false }
        return updated
      })
    }
  }

  // Cancel the in-flight turn. The client treats the abort as a clean stop and
  // resolves, so `sendMessage`'s finally clears loading and finalizes the bubble.
  const stopGeneration = () => {
    abortRef.current?.abort()
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent, files: File[] = []) => {
    e.preventDefault()

    sendMessage(input, files)
  }

  // Handle feedback submission
  const handleFeedbackSubmit = async (
    messageContent: string,
    question: string,
    feedbackType: "positive" | "negative",
    comment: string
  ) => {
    try {
      // Use ID token for API Gateway Cognito authorizer (not access token)
      const idToken = auth.user?.id_token

      if (!idToken) {
        throw new Error(copy.errors.authRequired)
      }

      await submitFeedback(
        {
          sessionId,
          message: messageContent,
          question,
          feedbackType,
          comment: comment || undefined,
        },
        idToken
      )

      console.log("Feedback submitted successfully")
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error submitting feedback:", err)
      setError(`${copy.errors.feedbackPrefix}: ${errorMessage}`)
    }
  }

  // Resolve a fresh, short-lived presigned download URL for one artifact at
  // click time (cloud build only — requires a Cognito ID token). Returns null
  // when not signed in, so ArtifactCard keeps its plain <a href> behavior in
  // the local build. Threaded down to each ArtifactCard via ChatMessages.
  const resolveArtifactDownloadUrl = (runId?: string, name?: string) => {
    const idToken = auth.user?.id_token
    if (!idToken || !runId || !name) return null
    return () => fetchArtifactDownloadUrl(sessionId, runId, name, idToken)
  }

  // Start a new chat: clear the thread and activate a fresh session id. The new
  // conversation is not saved until it has at least one message.
  const startNewChat = () => {
    startNew()
    setMessages([])
    setInput("")
    setError(null)
  }

  // Open a saved conversation: load its transcript and make it active. Selecting
  // the already-active conversation is a no-op. The in-flight stream is NOT
  // aborted on switch — it keeps running so its answer commits to Memory and
  // reappears on switch-back/reload; a stale write can't corrupt this view
  // because writers target their own message id (see updateMessage). Reopening
  // a still-streaming conversation re-appends the live bubble so a "Pensando…"
  // placeholder (and then live text) shows until its answer commits (see
  // reattachInFlight).
  const handleSelectSession = async (id: string) => {
    if (id === activeId) return
    const restored = await open(id)
    // El transcript guardado se rehidrata sin retocarlo: los paneles de
    // razonamiento se agrupan al renderizar, así que un turno reabierto se ve
    // igual que cuando corrió y no hace falta migrar nada.
    setMessages(reattachInFlight(restored, id))
    setInput("")
    setError(null)
  }

  // When reopening a conversation that still has a turn streaming, splice its
  // live in-flight bubbles back onto the restored transcript so the "Pensando…"
  // loader shows and — because the snapshot carries the turn's `assistantId` —
  // updateMessage keeps repainting it live on switch-back.
  //
  // Whether the current turn is already in the restore depends on the store, so
  // the reconcile is per-store (identity round-trips through neither store, so
  // we reason structurally rather than by matching ids or prompt text):
  //   - LOCAL store: never persists a turn until it completes, so the restore is
  //     the PRIOR transcript and the current turn is absent. Append-only —
  //     never strip (stripping a trailing turn here would delete a prior answer,
  //     e.g. when the user re-sends an identical prompt).
  //   - CLOUD (Memory): commits per message and folds tool-results into a single
  //     assistant bubble, so the in-flight turn is the exact tail — [.., prompt]
  //     before the reply commits, or [.., prompt, partial assistant] for a tool
  //     turn. No committed turn can be newer than the one still running, so that
  //     tail is provably the current turn: strip it by role/position (no content
  //     match) and re-append the live snapshot. Prior turns — even one with an
  //     identical prompt — sit earlier in the array and are untouched.
  const reattachInFlight = (restored: Message[], id: string): Message[] => {
    const inFlight = inFlightTurnRef.current
    if (!inFlight || inFlight.sessionId !== id) return restored

    if (LOCAL_AGENT) {
      return [...restored, inFlight.user, inFlight.assistant]
    }

    // Cloud tail cases (the current prompt commits at turn start, before the
    // reply): [.., prompt] before the reply, or [.., prompt, partial assistant]
    // for a tool turn. Strip that tail by position and re-append the snapshot.
    // (Narrow accepted edge: if the user switches away AND back in the sub-second
    // before the prompt itself commits, the tail is instead a PRIOR [user,
    // assistant] and the tool branch strips it FROM VIEW. Nothing durable is lost
    // — cloud `save` is a no-op, so the stripped array is never persisted and
    // Memory still holds the prior turn — but it stays hidden until this
    // conversation is next reopened or the page reloads (the turn-completion
    // refresh only reloads sidebar summaries, not the foreground transcript). A
    // durable fix needs Memory to round-trip the client message id — Stage 2.)
    const n = restored.length
    const last = restored[n - 1]
    const secondLast = restored[n - 2]
    let base = restored
    if (last?.role === "assistant" && secondLast?.role === "user") {
      base = restored.slice(0, n - 2) // tool turn: prompt + partial assistant committed
    } else if (last?.role === "user") {
      base = restored.slice(0, n - 1) // reply not committed yet
    }
    return [...base, inFlight.user, inFlight.assistant]
  }

  // Delete a saved conversation. If the turn currently streaming belongs to the
  // deleted conversation, abort it — it's gone, so there's nothing to keep alive
  // (keyed to the in-flight turn's own id, not the foreground id, so we never
  // abort a *different* conversation's background stream). If the deleted one is
  // active, fall back to a fresh empty chat so the thread never shows it.
  const handleDeleteSession = async (id: string) => {
    if (inFlightTurnRef.current?.sessionId === id) {
      abortRef.current?.abort()
    }
    await remove(id)
    if (id === activeId) {
      startNew()
      setMessages([])
      setInput("")
      setError(null)
    }
  }

  // Check if this is the initial state (no messages)
  const isInitialState = messages.length === 0

  // Check if there are any assistant messages
  const hasAssistantMessages = messages.some(message => message.role === "assistant")

  return (
    <div className="flex h-screen w-full flex-col bg-background text-foreground">
      {/* Fixed header — always full width on top */}
      <div className="flex-none">
          <ChatHeader />
      </div>

      {/* Below header: sidebar + main content side by side */}
      <div className="flex min-h-0 flex-1 bg-brand-surface">
        <AppSidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(v => !v)}
          sessions={summaries}
          activeId={activeId}
          streamingId={streamingId}
          onNewChat={startNewChat}
          canStartNewChat={hasAssistantMessages}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onRenameSession={(id, title) => void rename(id, title)}
          onClose={() => setSidebarOpen(false)}
          preferencesEnabled={preferencesApi}
        />
        <div className="m-5 flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-background">
          {/* Error banner */}
          {error && (
            <div
              role="alert"
              className="mx-4 mt-2 border-l-4 border-destructive bg-destructive/10 p-4"
            >
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Conditional layout based on whether there are messages */}
        {isInitialState ? (
          // Initial state — home screen per mockup: greeting + logo + subtitle + input + suggestions
          <>
            {/* Push welcome block to upper third */}
            <div className="grow-[2]" />

            {/* Centered welcome */}
            <div className="mb-8 flex flex-col items-center text-center">
              <p className="mb-2 font-sans text-lg font-normal text-brand-primary">
                {copy.welcome.greeting}
              </p>
              <img
                src={isDark ? logoAskmeWhite : logoAskme}
                alt={copy.welcome.title}
                className="mb-4 h-16"
              />
              <p className="max-w-md font-body text-sm font-semibold text-muted-foreground">
                {copy.welcome.subtitle}
              </p>
            </div>

            {/* Centered input */}
            <div className="mx-auto mb-6 w-full max-w-4xl px-4">
              <ChatInput
                input={input}
                setInput={setInput}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                onStop={stopGeneration}
              />
            </div>

            {/* Suggestions header + pills */}
            <div className="mx-auto mb-16 w-full max-w-4xl px-4">
              <div className="mb-6 flex flex-col items-center gap-1 text-muted-foreground">
                <span className="inline-flex size-12 items-center justify-center rounded-full bg-brand-surface">
                  <img src={isDark ? suggestionsIconWhite : suggestionsIcon} alt="" aria-hidden="true" className="size-10" />
                </span>
                <span className="font-body text-sm">{copy.welcome.suggestionsLabel}</span>
              </div>
              <Suggestions>
                {suggestionPills.map(suggestion => (
                  <Suggestion
                    key={suggestion}
                    suggestion={suggestion}
                    onSelect={text => sendMessage(text)}
                    disabled={isLoading}
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                  />
                ))}
              </Suggestions>
            </div>

            {/* More space below to push content upward */}
            <div className="grow-[5]" />
          </>
        ) : (
          // Chat in progress - normal layout
          <>
            {/* ASKme logo header in content area */}
            <div className="flex-none px-6 pt-4">
              <img src={isDark ? logoAskmeWhite : logoAskme} alt={copy.welcome.title} className="h-6" />
            </div>

            {/* Scrollable message area */}
            <div className="flex grow flex-col overflow-hidden">
              <ChatMessages
                messages={messages}
                sessionId={sessionId}
                userName={userName}
                messagePreviews={messagePreviews}
                onFeedbackSubmit={handleFeedbackSubmit}
                onResolveDownloadUrl={resolveArtifactDownloadUrl}
              />
            </div>

            {/* Fixed input area at bottom */}
            <div className="flex-none">
              <div className="mx-auto w-full max-w-4xl">
                <ChatInput
                  input={input}
                  setInput={setInput}
                  handleSubmit={handleSubmit}
                  isLoading={isLoading}
                  onStop={stopGeneration}
                />
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  )
}
